import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import { useEffect, useMemo } from 'react';

interface BiologicalAgeInterstitialProps {
  age?: string;
}

// ─── Age mapping ────────────────────────────────────────────────────────────

function getAgeExamples(ageSlug: string): { calendarAge: number; bioAge: number } {
  switch (ageSlug) {
    case 'under-20':
      return { calendarAge: 20, bioAge: 18 };
    case '20s':
      return { calendarAge: 26, bioAge: 22 };
    case '30s':
      return { calendarAge: 36, bioAge: 32 };
    case '40s':
      return { calendarAge: 46, bioAge: 42 };
    case '50s':
      return { calendarAge: 56, bioAge: 52 };
    case '60s':
      return { calendarAge: 66, bioAge: 62 };
    case '70+':
      return { calendarAge: 76, bioAge: 72 };
    default:
      return { calendarAge: 36, bioAge: 32 };
  }
}

// ─── Animated number display ────────────────────────────────────────────────

function AnimatedAge({ from, to, delay }: { from: number; to: number; delay: number }) {
  const value = useMotionValue(from);
  const rounded = useTransform(value, (v) => Math.round(v));

  useEffect(() => {
    const timeout = setTimeout(() => {
      animate(value, to, { duration: 0.8, ease: 'easeOut' });
    }, delay);
    return () => clearTimeout(timeout);
  }, [value, to, delay]);

  return <motion.span className="text-3xl font-bold">{rounded}</motion.span>;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function BiologicalAgeInterstitial({ age }: BiologicalAgeInterstitialProps) {
  const { calendarAge, bioAge } = useMemo(() => getAgeExamples(age ?? '30s'), [age]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-6">
      <motion.div
        className="flex w-full max-w-md flex-col items-center gap-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ willChange: 'transform, opacity' }}
      >
        {/* Headline */}
        <h3 className="text-center text-2xl font-bold leading-tight text-white md:text-3xl">
          See how your body is aging internally, and how it influences your risk.
        </h3>

        {/* Subtext */}
        <p className="text-center text-lg leading-relaxed text-white/65">
          Your free trial includes optional bloodwork at a local lab to further personalize your insights.
        </p>

        {/* Dual age comparison */}
        <div className="mt-2 flex justify-center gap-6 md:gap-10">
          {/* Calendar age */}
          <motion.div
            className="flex flex-col items-center"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ willChange: 'transform, opacity' }}
          >
            <span className="mb-2 text-sm text-white/55">Calendar Age</span>
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white/10 text-white">
              <span className="text-3xl font-bold">{calendarAge}</span>
            </div>
          </motion.div>

          {/* Biological age */}
          <motion.div
            className="flex flex-col items-center"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            style={{ willChange: 'transform, opacity' }}
          >
            <span className="mb-2 text-sm text-white/55">Biological Age</span>
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-[#7CF1C7]/15">
              <span className="text-[#7CF1C7]">
                <AnimatedAge from={calendarAge} to={bioAge} delay={1400} />
              </span>
            </div>
            <span className="mt-1 text-xs text-white/40">(example)</span>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
